/**
 * Robust RFC 4180 CSV Parser & Google Maps Scraper Field Mapper
 * Automatically decodes Instant Data Scraper / Web Scraper columns:
 * - xxVWCe: Business Name
 * - UsdlK: Phone number (sanitizes 0-prefixed Indian numbers to +91)
 * - MW4etd: Star rating
 * - lcr4fd href: Website URL
 * - W4Efsd / W4Efsd 4: Address / Location / Category
 */

export interface ParsedScrapedLead {
  businessName: string;
  contactName?: string;
  city: string;
  phone: string;
  email: string;
  website: string;
  rating?: number;
  category?: string;
  address?: string;
}

export function parseCsvRows(text: string): string[][] {
  if (text.charCodeAt(0) === 0xFEFF) {
    text = text.slice(1);
  }
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (inQuotes) {
      if (char === '"') {
        if (nextChar === '"') {
          // Escaped quote: "" -> "
          currentField += '"';
          i++; // Skip the next quote
        } else {
          // End of quoted field
          inQuotes = false;
        }
      } else {
        currentField += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        currentRow.push(currentField.trim());
        currentField = "";
      } else if (char === '\n' || char === '\r') {
        if (char === '\r' && nextChar === '\n') {
          i++; // Skip \n in \r\n
        }
        currentRow.push(currentField.trim());
        currentField = "";
        // Only push non-empty rows
        if (currentRow.some(val => val.length > 0)) {
          rows.push(currentRow);
        }
        currentRow = [];
      } else {
        currentField += char;
      }
    }
  }

  // Push final field/row if any
  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    if (currentRow.some(val => val.length > 0)) {
      rows.push(currentRow);
    }
  }

  return rows;
}

export function sanitizePhone(raw: string, isUaeHint = false): string {
  if (!raw) return "";
  
  // Quick reject if string is excessively long or contains repetitive symbols
  if (raw.length > 25 || /\+{2,}/.test(raw)) return "";

  // Keep only digits and +
  let cleaned = raw.replace(/[^0-9+]/g, "");

  // An international phone number (E.164) contains between 8 and 15 digits.
  // Anything with more than 15 digits is an encoded coordinate, SVG path, or session hash.
  const digitsOnly = cleaned.replace(/\D/g, "");
  if (digitsOnly.length < 8 || digitsOnly.length > 15) {
    return "";
  }
  
  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  // UAE mobile numbers starting with 05 (050, 052, 054, 055, 056, 058) - 10 digits
  if (/^05[024568]\d{7}$/.test(cleaned) || (isUaeHint && /^05\d{8}$/.test(cleaned))) {
    return `+971${cleaned.substring(1)}`;
  }

  // UAE landline starting with 04 (e.g. 04 352 1833) - 9 digits
  if (/^04\d{7}$/.test(cleaned) || (isUaeHint && /^04\d{7}$/.test(cleaned))) {
    return `+971${cleaned.substring(1)}`;
  }

  // UAE 971 prefix without +
  if (cleaned.startsWith("971") && (cleaned.length === 11 || cleaned.length === 12)) {
    return `+${cleaned}`;
  }

  // Handle leading 0 (common in Indian Google Maps phone numbers, e.g. 09845355345 or 08447786500)
  if (cleaned.startsWith("0") && cleaned.length === 11 && !cleaned.startsWith("04") && !cleaned.startsWith("05")) {
    cleaned = cleaned.substring(1);
  }

  // If 10 digits starting with 6, 7, 8, 9 format as Indian E.164 (+91)
  if (cleaned.length === 10 && /^[6-9]/.test(cleaned)) {
    return `+91${cleaned}`;
  }

  // If 12 digits starting with 91
  if (cleaned.length === 12 && cleaned.startsWith("91")) {
    return `+${cleaned}`;
  }

  return cleaned ? `+${cleaned}` : "";
}

// Backwards compatibility alias
export const sanitizeIndianPhone = sanitizePhone;

export function parseGoogleMapsCsv(csvText: string): ParsedScrapedLead[] {
  const rows = parseCsvRows(csvText);
  if (rows.length < 2) return [];

  const headers = rows[0].map(h => h.toLowerCase().trim());
  const leads: ParsedScrapedLead[] = [];

  // Find column indices
  const getIndex = (possibleNames: string[]) => {
    return headers.findIndex(h => possibleNames.some(name => h === name.toLowerCase() || h.includes(name.toLowerCase())));
  };

  // Google Maps scraper selector aliases
  const nameIdx = getIndex(["xxvwce", "business name", "agency name", "company name", "name", "title"]);
  const phoneIdx = getIndex(["usdlk", "phone", "mobile", "whatsapp", "contact number", "tel"]);
  const ratingIdx = getIndex(["mw4etd", "rating", "google rating", "stars"]);
  const websiteIdx = getIndex(["lcr4fd href", "website", "web", "site url"]);
  const categoryIdx = getIndex(["industry", "category_on_maps", "category", "type", "w4efsd", "primarytypedisplayname"]);
  const areaIdx = getIndex(["area", "locality", "neighborhood", "suburb"]);
  
  // Address candidate columns
  const addrIdx1 = getIndex(["w4efsd 4", "address"]);
  const addrIdx2 = getIndex(["w4efsd 3"]);
  const addrIdx3 = getIndex(["w4efsd 2"]);

  // Additional column indices for generic, trading, or standard CRM CSVs
  const companyIdx = getIndex(["company", "company name", "organization", "firm", "trading firm", "business"]);
  const emailIdx = getIndex(["email", "mail", "email address", "e-mail"]);
  const contactIdx = getIndex(["contact name", "contact person", "owner", "contact", "person", "client name"]);
  const cityIdx = getIndex(["city", "destination port", "port", "location", "town", "destination"]);
  const commodityIdx = getIndex(["commodity", "product", "item", "material", "service", "service type"]);

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length === 0) continue;

    // Detect if this row has UAE/Dubai context
    const fullRowText = row.join(" ").toLowerCase();
    const isUaeRow = /dubai|uae|marina|business bay|downtown|jvc|jumeirah|sheikh zayed|palm|difc|al barsha|motor city|abu dhabi|sharjah|\+?971/.test(fullRowText);

    // Extract business name & contact name
    let name = nameIdx !== -1 ? row[nameIdx] : "";
    let company = companyIdx !== -1 ? row[companyIdx] : "";
    let contactName = contactIdx !== -1 ? row[contactIdx] : "";

    // If company exists, use company as businessName and name as contactName
    let finalBusinessName = company || name;
    if (company && name && !contactName && company !== name) {
      contactName = name;
    }

    // If not found by index, look for first text cell that is not a URL, email, or rating
    if (!finalBusinessName) {
      finalBusinessName = row.find(c => c && !c.startsWith("http") && !c.includes("@") && !c.startsWith("+") && !/^\d+(\.\d+)?$/.test(c)) || "";
    }
    finalBusinessName = finalBusinessName.replace(/\s*[·•\-]\s*Visited link/gi, "").replace(/Visited link/gi, "").trim();
    if (!finalBusinessName || finalBusinessName === "Directions" || finalBusinessName === "Website") continue;

    // Extract Phone
    let rawPhone = phoneIdx !== -1 ? row[phoneIdx] : "";
    if (!rawPhone) {
      // Look for a cell that looks like a legitimate phone number (not coordinates or hashes)
      rawPhone = row.find(c => {
        const val = (c || "").trim();
        if (!val || val.length > 25 || /\+{2,}/.test(val)) return false;
        return /\b(?:\+?971[\s-]?)?[045]\d[\s-]?\d{3}[\s-]?\d{4}\b/.test(val) ||
               /\b(?:\+?91[\s-]?)?[6-9]\d{4}[\s-]?\d{5}\b/.test(val) ||
               /\b(?:\+?91[\s-]?)?[6-9]\d{9}\b/.test(val) ||
               /\b0[6-9]\d{9}\b/.test(val);
      }) || "";
    }
    const cleanPhone = sanitizePhone(rawPhone, isUaeRow);

    // Extract Email (check email column first, then scan row)
    let email = emailIdx !== -1 && row[emailIdx] ? row[emailIdx].trim() : "";
    if (!email) {
      const emailCell = row.find(c => c && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c.trim()));
      if (emailCell) email = emailCell.trim();
    }

    // Extract Rating
    let rating: number | undefined;
    if (ratingIdx !== -1 && row[ratingIdx]) {
      const parsed = parseFloat(row[ratingIdx]);
      if (!isNaN(parsed) && parsed > 0 && parsed <= 5) rating = parsed;
    }

    // Extract Website
    let website = "";
    if (websiteIdx !== -1 && row[websiteIdx] && row[websiteIdx].startsWith("http")) {
      website = row[websiteIdx];
    } else {
      const urlCell = row.find(c => c.startsWith("http") && !c.includes("google.com/maps") && !c.includes("gstatic.com"));
      if (urlCell) website = urlCell;
    }

    // Extract Category / Commodity / Industry
    let category = categoryIdx !== -1 ? row[categoryIdx] : "";
    if (category === "·" || !category) category = "";
    if (!category && commodityIdx !== -1 && row[commodityIdx]) {
      category = row[commodityIdx].trim();
    }

    // Extract Area / Locality
    const areaVal = areaIdx !== -1 ? row[areaIdx] : "";

    // Extract Address & City
    const addressParts: string[] = [];
    [addrIdx1, addrIdx2, addrIdx3].forEach(idx => {
      if (idx !== -1 && row[idx] && row[idx] !== "·" && !row[idx].startsWith("Open") && !row[idx].startsWith("Close")) {
        addressParts.push(row[idx]);
      }
    });
    const address = addressParts.join(", ") || "";

    // Explicit City column detection if available
    let city = cityIdx !== -1 && row[cityIdx] ? row[cityIdx].trim() : "";
    if (!city) {
      const lowerContext = (address + " " + finalBusinessName + " " + fullRowText).toLowerCase();
      if (lowerContext.includes("dubai") || isUaeRow) {
        city = "Dubai";
      } else if (lowerContext.includes("abu dhabi")) {
        city = "Abu Dhabi";
      } else if (lowerContext.includes("sharjah")) {
        city = "Sharjah";
      } else if (lowerContext.includes("mumbai")) {
        city = "Mumbai";
      } else if (lowerContext.includes("delhi") || lowerContext.includes("ncr") || lowerContext.includes("gurgaon")) {
        city = "Delhi NCR";
      } else if (lowerContext.includes("hyderabad")) {
        city = "Hyderabad";
      } else if (lowerContext.includes("pune")) {
        city = "Pune";
      } else if (lowerContext.includes("chennai")) {
        city = "Chennai";
      } else if (lowerContext.includes("bangalore") || lowerContext.includes("bengaluru") || lowerContext.includes("hsr") || lowerContext.includes("koramangala")) {
        city = "Bangalore";
      } else {
        city = cleanPhone.startsWith("+971") ? "Dubai" : "Bangalore";
      }
    }

    leads.push({
      businessName: finalBusinessName,
      contactName,
      city,
      phone: cleanPhone,
      email,
      website,
      rating,
      category,
      address,
    });
  }

  return leads;
}
